/**
 * Live stock, as the screen needs it.
 *
 * A single read-through call to `/api/datasource/stock`, never a table this
 * client queries directly — the price redaction happens on the server, and
 * `priced` on the response is what the screen trusts, not a client-side guess
 * about the caller's own role. Nothing here is polled automatically: a member
 * presses Refresh, the same as opening the screen for the first time.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { StockItem } from '@timely/domain';
import { apiPost, ApiError } from '../lib/api';

export interface StockRead {
    configured: boolean;
    items: StockItem[];
    priced: boolean;
    truncated: boolean;
    fetchedAt: string | null;
    statusDetail: string | null;
}

const UNCONFIGURED: StockRead = {
    configured: false, items: [], priced: false, truncated: false, fetchedAt: null, statusDetail: null,
};

interface StockResponse {
    configured: boolean;
    items?: StockItem[];
    priced?: boolean;
    truncated?: boolean;
    fetchedAt?: string;
    statusDetail?: string;
}

export function useStock(workspaceId: string) {
    const [state, setState] = useState<StockRead>(UNCONFIGURED);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Dropped on arrival if the company changed while it was in flight — the
    // same guard every other tenant-scoped hook in this app uses.
    const issuedFor = useRef(workspaceId);

    const load = useCallback(async () => {
        issuedFor.current = workspaceId;
        setLoading(true);
        setError(null);

        try {
            const response = await apiPost<StockResponse>('/api/datasource/stock', { workspaceId });
            if (issuedFor.current !== workspaceId) return;

            setState({
                configured: response.configured,
                items: response.items ?? [],
                priced: response.priced ?? false,
                truncated: response.truncated ?? false,
                fetchedAt: response.fetchedAt ?? null,
                statusDetail: response.statusDetail ?? null,
            });
        } catch (err) {
            if (issuedFor.current !== workspaceId) return;
            setState(UNCONFIGURED);
            setError(err instanceof ApiError ? err.message : 'We could not read your stock sheet just now.');
        } finally {
            if (issuedFor.current === workspaceId) setLoading(false);
        }
    }, [workspaceId]);

    useEffect(() => {
        void load();
    }, [load]);

    return { ...state, loading, error, refresh: load };
}
